import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailQueryDto, EmailQueryInterface } from '@zaoblako/shared/common/dtos/email-query.dto';
import type { ValidationErrorInterface } from '@zaoblako/shared/common/interfaces/validation-error.interface';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import {
  ExternalUserLkCreateDto,
  ExternalUserLkCreateInterface
} from '@zaoblako/shared/modules/security/dtos/external-user/lk/external-user-lk-create.dto';
import type { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';
import {
  ProfileRegisterDto,
  ProfileRegisterInterface
} from '@zaoblako/shared/modules/security/dtos/profile/profile-register.dto';
import { ProfileFormDto, ProfileFormInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-form.dto';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { PersonalAccountProfileService } from '@zaoblako/web/core/services/lk/personal-account-profile.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { PersonalAccountExternalUserService } from '@zaoblako/web/core/services/lk/personal-account-external-user.service';
import { LkEuAccessControlWidgetComponent } from '@zaoblako/web/shared/components/lk-eu-access-control-widget/lk-eu-access-control-widget.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { MenuItem } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputMaskModule } from 'primeng/inputmask';
import { StepsModule } from 'primeng/steps';
import { DividerModule } from 'primeng/divider';
import { Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'crm-lk-eu-external-user-create-form',
  host: { class: 'frm-filters-ui-v2' },
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SpinnerComponent,
    StepsModule,
    CheckboxModule,
    FormsModule,
    InputMaskModule,
    DividerModule,
    LkEuAccessControlWidgetComponent
  ],
  templateUrl: './lk-eu-external-user-create-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuExternalUserCreateFormComponent
  extends BaseFormComponent<ExternalUserLkCreateInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public email: EmailQueryInterface = <EmailQueryInterface>{};
  public errorEmail: ValidationErrorInterface<EmailQueryInterface>;

  public profile: ProfileFindInterface;
  public profileError: ValidationErrorInterface<ProfileFormInterface>;

  public profileRegister: ProfileRegisterInterface = <ProfileRegisterInterface>{};
  public errorProfileRegister: ValidationErrorInterface<ProfileRegisterInterface>;

  public user = <ExternalUserLkCreateInterface>{ profile: {}, invite: true };
  public dto = ExternalUserLkCreateDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public steps: MenuItem[] = [{ label: 'Email' }, { label: 'ФИО' }, { label: 'Доп. информация' }];
  public activeIndexStep = 0;

  public constructor(
    private readonly personalAccountExternalUserService: PersonalAccountExternalUserService,
    private readonly personalAccountProfileService: PersonalAccountProfileService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
  }

  public saveUser() {
    const closeForm = (user: ExternalUserFindInterface, withProfile = false) => {
      this.formStoreService.setLoading(false, this.index);
      if (!withProfile) {
        this.errorService.addSuccessMessage('Внешний пользователь создан');
      } else {
        this.errorService.addSuccessMessage(
          'Внешний пользователь создан',
          'Данный пользователь был найден в реестре системы, все его данные были автоматически заполнены'
        );
      }
      if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
        this.formStoreService.closeSidebar(this.index);
        this.router.navigate(['lk/external-user/external-user/card/' + user.id]).catch(console.error);
      } else {
        this.formStoreService.saveNext(user, this.index);
      }
    };

    if (!this.activeIndexStep) {
      const errors = this.validationService.parseAndValidate(this.email, EmailQueryDto)?.[1];
      if (errors) {
        this.errorEmail = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      this.personalAccountProfileService.testEmail(this.email).subscribe({
        next: (data) => {
          if (data.externalUser) {
            this.errorService.addCustomError('Пользователь с таким email уже существует');
          } else if (data.profile) {
            this.profile = data.profile;
            this.profileRegister.firstName = this.profile.firstName;
            this.profileRegister.middleName = this.profile.middleName;
            this.profileRegister.lastName = this.profile.lastName;
            this.activeIndexStep = 1;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          } else {
            this.activeIndexStep = 1;
            this.formStoreService.setLoading(false, this.index);
            this.cdRef.markForCheck();
          }
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 1) {
      this.profileRegister.email = this.email.email;

      if (this.profile?.id) {
        this.profile.lastName = this.profileRegister.lastName;
        this.profile.firstName = this.profileRegister.firstName;
        this.profile.middleName = this.profileRegister.middleName;
      }

      const errors = this.validationService.parseAndValidate(this.profileRegister, ProfileRegisterDto)?.[1];
      if (errors) {
        this.errorProfileRegister = errors.validationErrors;
        return;
      }

      this.formStoreService.setLoading(true, this.index);

      const req = this.profile?.id
        ? this.personalAccountProfileService.update<ProfileFormInterface, ProfileFindInterface>(this.profile)
        : this.personalAccountProfileService.create<ProfileFindInterface>(this.profileRegister);

      req.subscribe({
        next: (profile) => {
          this.profile = profile;
          this.activeIndexStep = 2;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
        }
      });
    } else if (this.activeIndexStep === 2) {
      this.user.profile = this.profile;

      if (this.profile.phone === '') {
        this.profile.phone = null;
      }

      const errors = this.validationService.parseAndValidate(this.profile, ProfileFormDto)?.[1];
      if (errors) {
        this.profileError = errors.validationErrors;
        return;
      }

      if (this.checkValidate(this.user).valid) {
        this.formStoreService.setLoading(true, this.index);

        let updatePhone$: Observable<ProfileFindInterface | void> = of(void 0);

        if (this.profile?.phone) {
          updatePhone$ = this.personalAccountProfileService.update<ProfileFormInterface, ProfileFindInterface>(
            this.profile
          );
        }

        updatePhone$
          .pipe(
            switchMap(() => {
              return this.personalAccountExternalUserService.create<ExternalUserFindInterface>(this.user);
            })
          )
          .subscribe({
            next: (user) => {
              closeForm(user);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    }
  }

  public back() {
    this.activeIndexStep--;
  }
}
